import React, { Fragment, ReactNode, useMemo, useState } from 'react';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import {
	ErrorCode,
	FinalCalculation,
	FinalCalculationProduct,
	Product,
	ReferencePrice,
} from '~/libs/infrastructure/schemas/czMaxPrice';
import { Product as GeneralProduct, parsePackageSize } from '~/Product';
import parseHumanFloat from '~/utils/Number/parseHumanFloat';

import ActivityIndicator from '~/UI/ActivityIndicator';
import ComplianceInfo from '~/CzSuklComplianceInfo';
import CopyGroupProductActionPanel from '~/CopyGroupProduct/CopyGroupProductActionPanel';
import ProductDetailModal, { PricesContainer } from '~/ProductDetailModal';
import ProductInstructions from '~/ProductInstructions';
import ProductGroupRemoveProductModal from '~/ProductGroupRemoveProductModal';
import ProductGroupNotificationSettingsModal from '~/ProductGroupNotificationSettingsModal';
import ProductGroupRenameModal from '~/ProductGroupRenameModal';
import ProductGroupDeleteModal from '~/ProductGroupDeleteModal';
import { SelectModal as ExchangeRateSelectModal } from '~/ExchangeRate';
import ProductGroupHistoryModal from '~/ProductGroupHistoryModal';
import { CreateModal as ProductGroupCreateModal } from '~/ProductGroup';
import { Typography } from '~/libs/ui/atoms/Typography';
import { ProductGroupShowAllItemsSwitch } from '~/libs/ui/molecules/ProductGroupShowAllItemsSwitch';
import { formatCountriesError } from '~/UI/formatCountriesError';

import { Header } from './src/components/Header';
import { ProductCalculation } from './src/components/ProductCalculation';
import { ProductParametersPanel, ReferenceValues, Status } from './src/components/ProductParametersPanel';
import { Table, ProductValues, ReferencePriceValue } from './src/components/Table';

import { useCzMaxPrice } from './src/adapters/czMaxPrice';
import { useFormValues } from './src/hooks/useFormValues';
import { buildProductUpdatesFromValues, Values } from './src/utils/buildProductUpdates';
import { useProducts } from './src/hooks/useProducts';

export enum ModalNames {
	history = 'history',
	notification = 'notification',
	rename = 'rename',
	delete = 'delete',
	create = 'create',
	copy = 'copy',
	exchangeRate = 'exchangeRate',
}

interface CzMaxPricePageProps {
	group: UserProductGroup;
	currency: string | null;
	detailProductId: string | null;
	detailProductTabName: string;
	productDetailLink(product: { id: string }): { pathname: string; search: string };
	onCloseDetail(): void;
	onShowDetail(productId: string, tabName: string): void;
	onCurrencyChange(currency: string): void;
	//onRemoved?: (group: UserProductGroup) => void;
	onQuery(a: string | null, b: string | null, c: string | null, d: boolean, e: boolean, f: boolean): void;
}

export default function CzMaxPricePage({
	group,
	currency,
	detailProductId,
	detailProductTabName = 'info',
	productDetailLink,
	onCloseDetail,
	onShowDetail,
	onCurrencyChange,
	//onRemoved,
	onQuery,
}: CzMaxPricePageProps) {
	const [removeProduct, setRemoveProduct] = useState<GeneralProduct | null>(null);
	const [modalName, setModalName] = useState<string | null>(null);
	const [lastSeenProductIdDetail, setLastSeenProductIdDetail] = useState<string | null>(null);

	const {
		czMaxPrice,
		isFetching,
		basketVersions,
		scheduleSave,
		flushSave,
		updateStatus,
		triggerExport,
		deleteProducts,
		exportProductsXml,
		exportEvidence,
		navigateAfterDelete,
	} = useCzMaxPrice(group.id);

	const { values, setReferenceValues, setProductValues, setProductSelected } = useFormValues(czMaxPrice);

	const {
		pricedProducts,
		czechProducts,
		suklReferencedProducts,
		otherProducts,
		alternativeProductsCount,
		referenceBasketVersion,
		checkedRows,
		lastSelectedProductId,
		checkRow,
		clearCheckedRows,
		computeSelectionUpdate,
		recordLastSelected,
	} = useProducts(czMaxPrice, basketVersions);

	const detailProduct = useMemo(
		() => czMaxPrice?.products.find((p) => p.product.id === detailProductId)?.product ?? null,
		[czMaxPrice, detailProductId]
	);

	const referencePackageSize = parsePackageSize(values.reference.packageSize.value).value;

	function scheduleSaveFromValues(nextValues: Values) {
		const { hasValidationError, products } = buildProductUpdatesFromValues(nextValues);
		const version = nextValues.reference.basketVersion;
		const packageSize = parsePackageSize(nextValues.reference.packageSize.value).value;
		if (!hasValidationError && version !== null) {
			scheduleSave(packageSize, version, products);
		}
	}

	function handleReferenceValuesChanged(reference: ReferenceValues) {
		const invokeSave = reference.basketVersion !== values.reference.basketVersion;
		const nextValues = { ...values, reference };
		setReferenceValues(reference);
		scheduleSaveFromValues(nextValues);

		if (invokeSave) {
			flushSave();
		}
	}

	function handleProductValuesChange(
		item: GeneralProduct,
		referencePrice: ReferencePriceValue | null,
		referencePrices: ReferencePrice[],
		packageSize: string,
		packageSizeForSingleUnit: string
	) {
		const currentValue = values.product[item.id];
		const invokeSave = currentValue.referencePrice.value !== referencePrice;
		const nextValues: Values = {
			reference: values.reference,
			product: {
				...values.product,
				[item.id]: {
					referencePrice: {
						value: referencePrice,
						isValid: referencePrices.length === 0 || referencePrice !== null,
					},
					packageSize: { value: packageSize, isValid: null },
					packageSizeForSingleUnit: { value: packageSizeForSingleUnit, isValid: null },
					selected: currentValue.selected,
				},
			},
		};
		setProductValues(item, referencePrice, referencePrices, packageSize, packageSizeForSingleUnit);
		scheduleSaveFromValues(nextValues);

		if (invokeSave) {
			flushSave();
		}
	}

	function handleProductSelected(item: GeneralProduct, selected: boolean, range: boolean) {
		const updatedProductValues = computeSelectionUpdate(item, selected, range, values.product);
		recordLastSelected(item);
		setProductSelected(updatedProductValues);
		scheduleSaveFromValues({ ...values, product: updatedProductValues });
	}

	function handleDeleteItems(productGroup: UserProductGroup, products: GeneralProduct[]) {
		return deleteProducts(productGroup, products).then(clearCheckedRows);
	}

	function handleProductsExportCz(currencyValue: string) {
		return exportProductsXml(group.id, currencyValue);
	}

	function handleEvidenceExport() {
		return exportEvidence(group.id);
	}

	function handleCzMaxPriceExport() {
		return flushSave().then(triggerExport);
	}

	function handleCloseDetail() {
		setLastSeenProductIdDetail(detailProductId);
		onCloseDetail();
	}

	function closeModal() {
		setModalName(null);
	}

	function renderCompliance(productId: string): ReactNode {
		const product = czMaxPrice?.products.find((p) => p.product.id === productId);
		return product !== undefined ? <ComplianceInfo compliance={product.productCompliance} /> : <Fragment />;
	}

	function renderCalculationEmptyResult(finalCalculation: FinalCalculation): ReactNode {
		const countryError = finalCalculation.findOnlyOneProductPerCountryError();
		switch (true) {
			case countryError !== null:
				return (
					<strong>
						Calculation requires at most one product for each country
						{formatCountriesError(countryError!.duplicateCountries)}
					</strong>
				);
			case finalCalculation.hasError(ErrorCode.RequiredProductSettingsMissingErrorCode):
				return <strong>Some products settings are missing</strong>;
			case finalCalculation.hasError(ErrorCode.ReferenceCountryProductMissingErrorCode):
				return <strong>Calculation requires at least one reference country product</strong>;
			case finalCalculation.hasError(ErrorCode.RequiresReferenceSettingsErrorCode):
				return <strong>Calculation requires reference settings</strong>;
			case finalCalculation.hasError(ErrorCode.MissingThreeReferenceCountryProductsErrorCode):
				return <strong>Calculation requires three reference country products</strong>;
			case finalCalculation.hasError(ErrorCode.IllegalPriceTypeSelectedErrorCode):
				return <strong>Some products have illegal price selected</strong>;
			default:
				return null;
		}
	}

	function createTopProductList(
		topProducts: FinalCalculationProduct[],
		allProducts: Product[],
		productValues: { [id: string]: ProductValues },
		refPackageSize: number | null
	) {
		return topProducts.map((product) => {
			const productPackage = productValues[product.id];
			const productPrices = allProducts
				.find((item) => item.product.id === product.id)
				?.referencePrices.find((item) => item.calculatedPrice.selected);
			const calculatedPrice = productPrices?.calculatedPrice ? productPrices.calculatedPrice.price : null;
			const parsedPackageSize =
				productPackage?.packageSize.value !== '' ? parseHumanFloat(productPackage?.packageSize.value) : refPackageSize;

			return {
				id: product.id,
				dataSource: product.dataSource,
				calculatedPrice,
				parsedPackageSize,
				referencePackageSize: refPackageSize,
			};
		});
	}

	return (
		<Fragment>
			<Header
				group={group}
				currency={currency}
				name={group.name}
				lastUpdateTime={group.itemsUpdatedTime}
				parentName={group.parent?.name}
				parentId={group.parent?.id}
				exchangeRatesSettings={group.exchangeRates.settings}
				notificationSettingsLink={group.links.notificationSettings}
				notificationFrequency={group.notification.settings.frequency}
				onNotificationModal={() => setModalName(ModalNames.notification)}
				onGroupHistoryModal={() => setModalName(ModalNames.history)}
				onExchangeRateModal={() => setModalName(ModalNames.exchangeRate)}
				onCopy={() => setModalName(ModalNames.copy)}
				onRename={() => setModalName(ModalNames.rename)}
				onDelete={() => setModalName(ModalNames.delete)}
				handleModulePriceExport={handleCzMaxPriceExport}
				handleProductsEvidenceExport={handleEvidenceExport}
				handleProductsExport={handleProductsExportCz}
			/>
			<section className='pb-10'>
				{czMaxPrice === null && isFetching && <ActivityIndicator />}
				{czMaxPrice !== null && referenceBasketVersion !== null && (
					<div className='relative'>
						{updateStatus == Status.saving && (
							<div className='absolute top-0 left-0 w-full h-full bg-white/75 flex items-center justify-center z-10'>
								<ActivityIndicator />
							</div>
						)}
						<ProductCalculation
							topProducts={createTopProductList(
								czMaxPrice.finalCalculation.fromProducts,
								czMaxPrice.products,
								values.product,
								referencePackageSize
							)}
							threeLowestPricesAvg={czMaxPrice.finalCalculation.threeLowestPricesAvg}
							secondAndThirdLowestPriceAvg={czMaxPrice.finalCalculation.secondAndThirdLowestPriceAvg}
							deviation={czMaxPrice.finalCalculation.deviation}
							exFactoryPrice={czMaxPrice.selectExFactoryPrice()}
							emptyReason={renderCalculationEmptyResult(czMaxPrice.finalCalculation)}
						/>
					</div>
				)}

				<ProductParametersPanel
					values={values.reference}
					updateStatus={updateStatus}
					versions={basketVersions}
					onChange={handleReferenceValuesChanged}
					onSave={flushSave}
				/>

				{czMaxPrice?.products.length === 0 && <ProductInstructions />}

				{(czMaxPrice?.products?.length || 0) > 0 && (
					<>
						<div className='relative max-w-[1260px] mx-auto pt-5 pb-2 px-[40px]'>
							<Typography level={2} weight='font-light' color='cBlue-800'>
								Czech product
							</Typography>
						</div>
						<Table
							products={czechProducts}
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							checkedRows={checkedRows}
							checkRow={checkRow}
							lastSelectedProductId={lastSelectedProductId}
							productValues={values.product}
							referencePackageSize={referencePackageSize}
							onChange={handleProductValuesChange}
							onSave={flushSave}
							onSelect={handleProductSelected}
						/>
					</>
				)}

				{(czMaxPrice?.products?.length || 0) > 0 && suklReferencedProducts.length > 0 && (
					<>
						<div className='max-w-[1260px] mx-auto pt-5 pb-2 px-[40px] flex flex-nowrap flex-row justify-between items-end'>
							<Typography level={2} weight='font-light' className='pr-4 whitespace-nowrap'>
								Reference basket products
							</Typography>
							<ProductGroupShowAllItemsSwitch alternativeProductsCount={alternativeProductsCount} />
						</div>
						<Table
							products={suklReferencedProducts}
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							checkedRows={checkedRows}
							checkRow={checkRow}
							lastSelectedProductId={lastSelectedProductId}
							productValues={values.product}
							referencePackageSize={referencePackageSize}
							onChange={handleProductValuesChange}
							onSave={flushSave}
							onSelect={handleProductSelected}
						/>
					</>
				)}

				{otherProducts.length > 0 && (
					<>
						<div className='relative max-w-[1260px] mx-auto pt-5 pb-2 px-[40px]'>
							<Typography level={2} weight='font-light'>
								Other
							</Typography>
						</div>
						{pricedProducts.length > 0 && (
							<Table
								disableSelection
								detailLink={productDetailLink}
								highlightProductId={lastSeenProductIdDetail}
								checkedRows={checkedRows}
								checkRow={checkRow}
								lastSelectedProductId={lastSelectedProductId}
								products={otherProducts}
								productValues={values.product}
								referencePackageSize={referencePackageSize}
								onChange={handleProductValuesChange}
								onSave={flushSave}
								onSelect={handleProductSelected}
							/>
						)}
					</>
				)}

				{removeProduct !== null && (
					<ProductGroupRemoveProductModal
						group={group}
						product={removeProduct}
						onClose={() => setRemoveProduct(null)}
						onConfirm={async () => flushSave()}
						onSuccess={() => setRemoveProduct(null)}
					/>
				)}
				{checkedRows.length > 0 && (
					<CopyGroupProductActionPanel
						group={group}
						products={checkedRows}
						selectedProductsCount={checkedRows.length}
						onClickCancelDelete={clearCheckedRows}
						onClickRemoveRow={handleDeleteItems}
						onProductsCopied={clearCheckedRows}
					/>
				)}
			</section>

			{detailProductId !== null && (
				<PricesContainer currency={currency} productId={detailProductId}>
					<ProductDetailModal
						currency={currency}
						groupId={group.id}
						isFetching={false}
						product={detailProduct}
						productId={detailProductId}
						products={pricedProducts}
						selectedTabName={detailProductTabName}
						shareLink={`${window.location.origin}/search/${encodeURIComponent(detailProductId)}/${
							detailProductTabName === 'info' ? '' : encodeURIComponent(detailProductTabName)
						}`}
						status={renderCompliance(detailProductId)}
						onClose={handleCloseDetail}
						onCurrencyChange={onCurrencyChange}
						onChange={(productId) => onShowDetail(productId, detailProductTabName)}
						onSelectTab={(tabName) => onShowDetail(detailProductId, tabName)}
					/>
				</PricesContainer>
			)}
			{modalName === ModalNames.notification && (
				<ProductGroupNotificationSettingsModal
					group={group}
					onClose={closeModal}
					onChange={async () => {
						await onQuery(null, currency, 'ex-factory', false, false, false);
						closeModal();
					}}
				/>
			)}
			{modalName === ModalNames.copy && (
				<ProductGroupCreateModal
					groupType={group.type}
					templateGroup={group}
					onClose={closeModal}
					onSave={async () => {
						await flushSave();
						closeModal();
					}}
				/>
			)}
			{modalName === ModalNames.rename && (
				<ProductGroupRenameModal
					group={group}
					onSave={async () => {
						setModalName(null);
						return { moduleError: null };
					}}
					onClose={closeModal}
				/>
			)}
			{modalName === ModalNames.delete && (
				<ProductGroupDeleteModal
					group={group}
					onClose={closeModal}
					onConfirm={async (productGroup) => {
						await navigateAfterDelete(productGroup);
						closeModal();
					}}
				/>
			)}
			{modalName === ModalNames.history && <ProductGroupHistoryModal group={group} onClose={closeModal} />}
			{modalName === ModalNames.exchangeRate && (
				<ExchangeRateSelectModal group={group} initiallySelectedDate={null} onClose={closeModal} />
			)}
		</Fragment>
	);
}
